import { FC, useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { IPenaltyBonusItem, IPenaltyBonusSummary, PenaltiesBonuses, penaltyBonusService } from '../../../entities/penaltyBonus'
import { useGlobalMessageActions } from '../../../entities/globalMessage'
import { useMyActions } from '../../../entities/my'
import { AuthError } from '../../../shared/lib/error/AuthError'
import { MyError } from '../../../shared/lib/error/MyError'
import { Calendar } from '../../../shared/ui/calendar'
import { LoaderSpinner } from '../../../shared/ui/spinner'
import { getDateUTC } from '../../../shared/lib/helpers/getDateUTC'
import { STAFF_USER_ROUTE } from '../../../app/router/routes'
import { OpenContainer } from '../../../features/openContainer'
import classes from './staffPenaltiesBonuses.module.scss'

export const StaffPenaltiesBonuses: FC = () => {
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const [items, setItems] = useState<IPenaltyBonusItem[]>([])
    const [summary, setSummary] = useState<IPenaltyBonusSummary | null>(null)
    const [loading, setLoading] = useState(false)
    const [forbidden, setForbidden] = useState(false)

    const load = useCallback(async (from: string, to: string) => {
        try {
            setLoading(true)
            const result = await penaltyBonusService.getStaffPenaltiesBonuses(from, to)
            setItems(result.items.map(item => ({
                id: item.id,
                type: item.type,
                amount: item.amount,
                comment: item.comment,
                actualAt: item.actual_at,
                createdAt: item.created_at,
                userId: item.user_id,
                userName: item.user_name,
                createdByUserId: item.created_by_user_id,
                createdByName: item.created_by_name,
                isPaid: item.is_paid,
            })))
            setSummary({penalties: result.summary.penalties, bonuses: result.summary.bonuses})
        }
        catch(error) {
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError && error.status === 403){
                setForbidden(true)
            }
            else{
                setGlobalMessage({message: error instanceof Error ? error.message : 'Не удалось получить премирования и депремирования', type: 'error'})
            }
        }
        finally {
            setLoading(false)
        }
    }, [setGlobalMessage, setIsAuth])

    if(forbidden) return null

    return <section className={classes.widget}>
        <OpenContainer
            title="Премирование и депремирование работников"
            allowOverflow
            icon={<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M8 12H16M12 8V16" /></svg>}
        >
            <section className={classes.content}>
                <Calendar onDateRangeSelect={(startDate, endDate) => {
                    if(startDate && endDate) load(getDateUTC(startDate), getDateUTC(endDate))
                    if(!startDate && !endDate){
                        setItems([])
                        setSummary(null)
                    }
                }} />
                {loading ? <section className={classes.loader}><LoaderSpinner /></section> : items.length > 0 && summary && <section className={classes.data}>
                    <PenaltiesBonuses
                        items={items}
                        summary={summary}
                        onDelete={async () => undefined}
                        canDelete={false}
                        bonusLabel="Премирование"
                        penaltyLabel="Депремирование"
                        renderMeta={item => <section className={classes.itemMeta}>
                            <span>Сотрудник: {item.userId
                                ? <Link className={classes.staffLink} to={STAFF_USER_ROUTE.path.replace(':user_id', String(item.userId))}>{item.userName || `Сотрудник ${item.userId}`}</Link>
                                : item.userName || '—'}</span>
                            <span>Назначил: {item.createdByUserId
                                ? <Link className={classes.staffLink} to={STAFF_USER_ROUTE.path.replace(':user_id', String(item.createdByUserId))}>{item.createdByName || `Сотрудник ${item.createdByUserId}`}</Link>
                                : item.createdByName || '—'}</span>
                            <span>Статус выплаты: {item.isPaid ? 'Выплачено' : 'Не выплачено'}</span>
                        </section>}
                    />
                </section>}
            </section>
        </OpenContainer>
    </section>
}
